import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { METHODOLOGY, IMAGES, WHATSAPP_URL } from "../lib/content";

export default function Metodologia() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const lineScale = useTransform(scrollYProgress, [0.05, 0.95], [0, 1]);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-24 bg-sca-black text-white overflow-hidden">
        <img src={IMAGES.blueprint} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/85 to-black" />
        <div className="absolute inset-0 sca-grid-lines opacity-25" />

        <div className="sca-container relative">
          <Reveal>
            <p className="sca-overline text-white/65 mb-6">[ Metodologia · 6 etapas ]</p>
            <h1 className="font-sans text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tightest max-w-[20ch]">
              Método, planejamento e <span className="sca-serif text-sca-red">controle</span> em todas as etapas da obra.
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
              A obra precisa funcionar como um sistema organizado. Conheça as seis etapas que estruturam
              a execução técnica da SCA.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section ref={ref} className="relative sca-section bg-white" data-testid="methodology-timeline">
        <div className="sca-container">
          <div className="relative">
            {/* Vertical progress line */}
            <div className="absolute left-[28px] md:left-1/2 md:-translate-x-px top-0 bottom-0 w-px bg-sca-line" aria-hidden />
            <motion.div
              style={{ scaleY: lineScale, transformOrigin: "top" }}
              className="absolute left-[28px] md:left-1/2 md:-translate-x-px top-0 bottom-0 w-px bg-sca-red"
              aria-hidden
            />

            <div className="space-y-16 md:space-y-24">
              {METHODOLOGY.map((step, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <Reveal key={step.n} delay={i * 0.05}>
                    <div className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center ${isLeft ? "" : "md:[&>div:first-child]:order-2"}`}>
                      {/* Dot */}
                      <div className="absolute left-[20px] md:left-1/2 md:-translate-x-1/2 top-1.5 w-4 h-4 bg-sca-red border-4 border-white shadow-[0_0_0_1px_rgba(200,16,46,0.4)]" aria-hidden />

                      <div className={`pl-16 md:pl-0 ${isLeft ? "md:text-right md:pr-16" : "md:text-left md:pl-16"}`}>
                        <span className="font-mono text-sm md:text-base tracking-[0.3em] text-sca-red font-bold">/ {step.n}</span>
                        <h3 className="font-sans text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest text-sca-black mt-3 mb-5 leading-tight">
                          {step.title}
                        </h3>
                        <p className="sca-body max-w-md md:ml-auto">{step.desc}</p>
                      </div>

                      <div className="pl-16 md:pl-0">
                        <div className="aspect-[4/3] overflow-hidden bg-sca-mist">
                          <img
                            src={[IMAGES.blueprint, IMAGES.helmet, IMAGES.warehouse, IMAGES.steelFrame, IMAGES.management, IMAGES.concrete][i]}
                            alt={step.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sca-section bg-sca-black text-white">
        <div className="sca-container grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <p className="sca-overline text-white/60 mb-6">[ Aplicar esta metodologia ]</p>
            <h2 className="font-sans text-3xl md:text-5xl lg:text-6xl font-bold tracking-tightest leading-[1.05] max-w-3xl">
              Quer aplicar essa metodologia na <span className="sca-serif text-sca-red">sua obra</span>?
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:flex md:justify-end">
            <a href={WHATSAPP_URL("Quero aplicar a metodologia SCA na minha obra.")} target="_blank" rel="noreferrer" className="btn-sca-primary" data-testid="metodologia-cta">
              Falar com a engenharia <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
